import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Loader2 } from "lucide-react";
import { Section } from "@/components/mobile/Section";

export const STAGES = ["Lead", "Qualified", "Proposal", "Won", "Lost"];
export const money = (v) => (v || v === 0 ? `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : "");

export function StageSelect({ deal, onMoved, testid }) {
  const move = async (stage) => {
    try { await api.patch(`/crm/deals/${deal.id}`, { stage }); toast.success(`Moved to ${stage}`); onMoved(stage); }
    catch { toast.error("Could not move deal"); }
  };
  return (
    <select value={deal.stage} onChange={(e) => move(e.target.value)} data-testid={testid} onClick={(e) => e.stopPropagation()}
      className="h-9 px-2 text-sm border border-zinc-300 bg-white outline-none focus:border-[#D4AF37]">
      {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
    </select>
  );
}

export function MobileDeals({ customerId, deals, onChange }) {
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState("");
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);

  const add = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    try {
      await api.post(`/crm/customers/${customerId}/deals`, { title: title.trim(), stage: "Lead", amount: amount ? Number(amount) : null });
      toast.success("Deal added");
      setTitle(""); setAmount(""); setAdding(false); onChange();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not add deal"); }
    finally { setSaving(false); }
  };

  return (
    <Section title={`Deals (${deals.length})`} action={<button onClick={() => setAdding((a) => !a)} data-testid="m-deal-add-toggle" className="flex items-center gap-1 text-xs font-semibold text-[#0F172A]"><Plus className="h-4 w-4" />Add</button>}>
      {adding && (
        <form onSubmit={add} className="mb-3 space-y-2" data-testid="m-deal-form">
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Deal title" required data-testid="m-deal-title" className="w-full h-11 px-3 border border-zinc-300 text-base outline-none focus:border-[#D4AF37]" />
          <div className="flex gap-2">
            <input value={amount} onChange={(e) => setAmount(e.target.value)} type="number" inputMode="decimal" placeholder="Amount ($)" data-testid="m-deal-amount" className="flex-1 h-11 px-3 border border-zinc-300 text-base outline-none focus:border-[#D4AF37]" />
            <button disabled={saving} data-testid="m-deal-save" className="px-5 bg-[#0F172A] text-white text-sm font-medium flex items-center gap-2">{saving && <Loader2 className="h-4 w-4 animate-spin" />}Save</button>
          </div>
        </form>
      )}
      {deals.length === 0 && !adding ? <p className="text-sm text-zinc-400">No deals yet.</p> : (
        <div className="divide-y divide-zinc-100">
          {deals.map((d) => (
            <div key={d.id} className="py-2.5 flex items-center gap-3" data-testid={`m-deal-${d.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#0F172A] truncate">{d.title}</p>
                {d.amount != null && <p className="text-xs text-zinc-500">{money(d.amount)}</p>}
              </div>
              <StageSelect deal={d} onMoved={onChange} testid={`m-deal-stage-${d.id}`} />
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}
